import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Bell, ArrowLeft, CheckCheck, Wrench, Truck, 
  Users, ExternalLink, Inbox, Package, FolderKanban, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';

const APP_ICONS = {
  calibration_cms: Wrench,
  supplier_mgmt: Truck,
  user_mgmt: Users,
  inventory_mgmt: Package,
  project_mgmt: FolderKanban,
};

const APP_COLORS = {
  calibration_cms: 'bg-blue-100 text-blue-600',
  supplier_mgmt: 'bg-emerald-100 text-emerald-600',
  user_mgmt: 'bg-purple-100 text-purple-600',
  inventory_mgmt: 'bg-amber-100 text-amber-600',
  project_mgmt: 'bg-cyan-100 text-cyan-600',
};

const APP_LABELS = {
  calibration_cms: 'Calibration',
  supplier_mgmt: 'Procurement',
  user_mgmt: 'Users',
  inventory_mgmt: 'Inventory',
  project_mgmt: 'Programs',
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    loadNotifications();
  }, [filter]);

  const loadNotifications = async () => {
    try {
      const res = await notificationsAPI.list(filter === 'unread');
      setNotifications(res.data);
    } catch (err) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#0F172A]" />
                <h1 className="text-lg font-semibold text-[#0F172A]">Notifications</h1>
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white text-[10px]">{unreadCount} unread</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilter(filter === 'all' ? 'unread' : 'all')}
                className="text-xs"
              >
                {filter === 'all' ? 'Show Unread Only' : 'Show All'}
              </Button>
              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="text-xs gap-1"
                >
                  <CheckCheck className="w-3 h-3" /> Mark All Read
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No notifications</p>
            <p className="text-xs text-slate-400 mt-1">
              {filter === 'unread' ? 'All caught up!' : 'Notifications will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => {
              const Icon = APP_ICONS[notification.app] || Bell;
              const colorClass = APP_COLORS[notification.app] || 'bg-slate-100 text-slate-600';
              const appLabel = APP_LABELS[notification.app] || notification.app;
              
              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    notification.is_read 
                      ? 'bg-white border-slate-200' 
                      : 'bg-blue-50/50 border-blue-200 hover:bg-blue-50'
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{appLabel}</Badge>
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <h3 className="font-medium text-[#0F172A] mt-1">{notification.title}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{notification.message}</p>
                      <p className="text-[10px] text-slate-400 mt-2">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                    </div>
                    {notification.link && (
                      <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
